import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCategories, getMarket } from "../lib/api";
import { useCurrency } from "../lib/currency";
import { Sparkline } from "../components/Sparkline";
import { ArrowUp, ArrowDown } from "lucide-react";

export default function Dashboard() {
    const [categories, setCategories] = useState([]);
    const [active, setActive] = useState("stocks");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const nav = useNavigate();
    const { format } = useCurrency();

    useEffect(() => {
        getCategories().then(setCategories).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        getMarket(active)
            .then(setRows)
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
        // Auto-refresh every 60s
        const id = setInterval(() => {
            getMarket(active).then(setRows).catch(() => {});
        }, 60000);
        return () => clearInterval(id);
    }, [active]);

    return (
        <div data-testid="dashboard-page" className="px-4 py-6 max-w-[1600px] mx-auto fade-up">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                    <div className="text-[10px] text-mute tracking-[0.2em] mb-1">
                        FINANCIAL MARKETS / LIVE TERMINAL
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight brand-gradient-text">
                        Mercati globali
                    </h1>
                    <p className="text-dim text-sm mt-2 max-w-xl">
                        Segnali tecnici real-time su azioni, crypto, materie prime, energia e indici. RSI, Bollinger, MACD, Fibonacci, posizionamento long/short.
                    </p>
                </div>
                <div className="grid grid-cols-3 gap-3 text-xs">
                    <Stat label="ASSET" value={categories.reduce((a, c) => a + c.count, 0)} />
                    <Stat label="CATEGORIE" value={categories.length} />
                    <Stat label="UPDATE" value="60s" accent />
                </div>
            </div>

            {/* Category tabs */}
            <div data-testid="category-tabs" className="border-b border-soft flex flex-wrap mb-4">
                {categories.map(c => {
                    const isActive = c.id === active;
                    return (
                        <button
                            key={c.id}
                            data-testid={`tab-${c.id}`}
                            onClick={() => setActive(c.id)}
                            className={`px-4 py-2.5 text-xs font-semibold tracking-wider border-b-2 -mb-px ${
                                isActive ? "border-amber" : "border-transparent text-dim hover:text-foreground"
                            }`}
                            style={isActive ? { borderColor: "var(--amber)", color: "var(--amber)" } : undefined}
                        >
                            {c.label.toUpperCase()}
                            <span className="ml-2 text-mute tnum">{c.count}</span>
                        </button>
                    );
                })}
            </div>

            {/* Table */}
            <div data-testid="market-table" className="border border-soft bg-elev">
                <div className="grid grid-cols-12 px-4 py-2.5 text-[10px] tracking-widest text-mute border-b border-soft uppercase">
                    <div className="col-span-3">Simbolo</div>
                    <div className="col-span-3">Nome</div>
                    <div className="col-span-2 text-right">Prezzo</div>
                    <div className="col-span-2 text-right">24h %</div>
                    <div className="col-span-2 text-right">Trend</div>
                </div>
                {loading && (
                    <div className="px-4 py-12 text-center text-mute text-sm">Caricamento dati di mercato…</div>
                )}
                {!loading && rows.map((r, i) => {
                    const up = (r.change_pct || 0) >= 0;
                    return (
                        <button
                            key={r.symbol}
                            data-testid={`asset-row-${r.symbol}`}
                            onClick={() => nav(`/asset/${encodeURIComponent(r.symbol)}`)}
                            className="w-full grid grid-cols-12 px-4 py-3 text-sm border-b border-soft items-center hover:bg-elev2 text-left"
                            style={{ animationDelay: `${i * 25}ms` }}
                        >
                            <div className="col-span-3 font-semibold text-amber">{r.symbol}</div>
                            <div className="col-span-3 text-dim text-xs truncate">{r.name}</div>
                            <div className="col-span-2 text-right tnum">
                                {format(r.price, { decimals: 4 })}
                            </div>
                            <div className={`col-span-2 text-right tnum flex items-center justify-end gap-1 ${up ? "text-green-up" : "text-red-down"}`}>
                                {up ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                                {Math.abs(r.change_pct || 0).toFixed(2)}%
                            </div>
                            <div className="col-span-2 flex justify-end">
                                <Sparkline data={r.sparkline} />
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

const Stat = ({ label, value, accent }) => (
    <div className="border border-soft bg-elev px-3 py-2">
        <div className="text-[9px] text-mute tracking-widest">{label}</div>
        <div className={`text-xl font-bold tnum ${accent ? "" : ""}`} style={accent ? { color: "var(--amber)" } : undefined}>
            {value}
        </div>
    </div>
);
